import type { EmployeeProfileResponse } from '../../api/types';
import { formatCalendarDate } from './attributeDisplay';
import { ProfilePhoto } from './ProfilePhoto';
import styles from './ProfileHeader.module.css';

/**
 * The profile header (docs/UX_ARCHITECTURE.md §3.1): who this person is and
 * where they sit, answerable at a glance.
 *
 * It carries identity and organisational placement, and nothing else. No
 * ratings, no scores, no talent labels — the first thing a reader meets on
 * a profile should be a person, not a verdict. Internal identifiers stay
 * out entirely; the employee number is shown because it is how people
 * actually refer to a record, but the UUID never appears on screen.
 *
 * Every organisational field is optional, because the domain says so: an
 * employee may have no current role and no current team
 * (docs/DOMAIN_MODEL.md §2.4, "at most one", not "exactly one"). Those
 * cases are labelled, not blanked.
 */
export function ProfileHeader({ profile }: { profile: EmployeeProfileResponse }) {
  const statusLabel: Record<EmployeeProfileResponse['employmentStatus'], string> = {
    active: 'Active',
    leave: 'On leave',
    former: 'Former employee',
  };

  return (
    <header className={styles.header}>
      <ProfilePhoto employeeId={profile.id} fullName={profile.fullName} canManage={profile.viewerCanManageDocuments} />

      <div className={styles.identity}>
        <h1 className={styles.name}>{profile.fullName}</h1>
        <p className={styles.role}>
          {profile.currentRole ? profile.currentRole.name : <span className={styles.absent}>No current role</span>}
          {profile.currentLevel ? <span className={styles.level}>{profile.currentLevel}</span> : null}
        </p>
      </div>

      <dl className={styles.facts}>
        <div className={styles.fact}>
          <dt>Team</dt>
          <dd>{profile.currentTeam ? profile.currentTeam.name : <span className={styles.absent}>Unassigned</span>}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Department</dt>
          <dd>
            {profile.currentTeam ? (
              profile.currentTeam.departmentName
            ) : (
              <span className={styles.absent}>—</span>
            )}
          </dd>
        </div>
        <div className={styles.fact}>
          <dt>Status</dt>
          <dd>{statusLabel[profile.employmentStatus]}</dd>
        </div>
        <div className={styles.fact}>
          <dt>Joined</dt>
          <dd>{formatCalendarDate(profile.employmentStartedOn)}</dd>
        </div>
        {profile.employeeNumber ? (
          <div className={styles.fact}>
            <dt>Employee number</dt>
            <dd className={styles.mono}>{profile.employeeNumber}</dd>
          </div>
        ) : null}
      </dl>
    </header>
  );
}
